// v1.1: running spend totals for the Booths screen — per show, plus a grand total.
//
// `total_spent` is FREE TEXT by design (v0.5: "$1,200", "1200", "1,200.50", and
// whatever else a buyer types one-handed on a show floor). Totalling it is therefore
// best-effort, and the rule that matters is: NOTHING IS SILENTLY DROPPED. A value we
// can't read with confidence is COUNTED and surfaced next to the total, so the number
// on screen is either right or visibly incomplete — never quietly wrong.
//
// Blank is not an error: most booths are looked at, not bought from.

import type { BoothRecord } from '../db/schema';

export interface ShowTotal {
  show: string;
  booths: number;
  /** Sum of the parseable amounts on this show's booths. */
  total: number;
  /** Non-blank total_spent values we could not read (counted, never dropped). */
  unparsed: number;
}

export interface SpendTotals {
  shows: ShowTotal[];
  grand: number;
  booths: number;
  unparsed: number;
}

/**
 * Read a money amount out of free text, or null when we can't be sure.
 *
 * Accepts: "$1,200" · "1200" · "1,200.50" · "USD 1200" · "1200 ea".
 * Rejects (→ null, i.e. flagged): "TBD" · "two grand" · "$400 + $300" ·
 * "3 cases @ $400" — anything where a SECOND number follows the one we read, since
 * "the leading number" is then just as likely to be a quantity as the spend.
 * That's the trustworthiness rule: when in doubt, flag it rather than half-count it.
 */
export function parseSpend(raw: string | undefined | null): number | null {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  // Strip thousands separators + spaces, then any leading currency/label run.
  const cleaned = s.replace(/[,\s]/g, '').replace(/^[^0-9.-]+/, '');
  const m = /^-?\d+(?:\.\d+)?/.exec(cleaned);
  if (!m) return null;
  const rest = cleaned.slice(m[0].length);
  if (/\d/.test(rest)) return null; // a second number — ambiguous, flag it
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : null;
}

/**
 * Totals over LIVE booths. Callers pass the same collection the list renders
 * (`listBooths()` already hides tombstones), so a deleted booth can't move the
 * number. Only shows with captured booths appear — no row of $0 placeholders.
 */
export function computeSpendTotals(booths: BoothRecord[]): SpendTotals {
  const byShow = new Map<string, ShowTotal>();
  const out: SpendTotals = { shows: [], grand: 0, booths: 0, unparsed: 0 };

  for (const b of booths) {
    if (b.deleted) continue; // belt-and-braces: never let a tombstone count
    const show = (b.show ?? '').trim() || '—';
    let row = byShow.get(show);
    if (!row) {
      row = { show, booths: 0, total: 0, unparsed: 0 };
      byShow.set(show, row);
      out.shows.push(row);
    }
    row.booths++;
    out.booths++;

    const raw = (b.total_spent ?? '').trim();
    if (!raw) continue; // blank = simply not counted
    const v = parseSpend(raw);
    if (v === null) {
      row.unparsed++;
      out.unparsed++;
      continue;
    }
    row.total += v;
    out.grand += v;
  }

  // Biggest spend first — that's the question this block answers.
  out.shows.sort((a, b) => b.total - a.total || b.booths - a.booths);
  return out;
}

/** "$1,200" / "$1,200.50" — cents only when there are any. */
export function money(n: number): string {
  const cents = Math.abs(n % 1) > 0.0001;
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

/** "2 entries not counted" — the honesty line. Empty string when everything read. */
export function unparsedNote(n: number): string {
  if (!n) return '';
  return `${n} ${n === 1 ? 'entry' : 'entries'} not counted (amount unreadable)`;
}
